import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Lock,
  Loader2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "sonner";

// Bundled PDF.js worker (avoids CDN dependency and version mismatches).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

/**
 * ReaderPage — protected read-only PDF viewer for course theory material.
 *
 * The PDF is fetched with the user's Bearer token, converted to a blob URL
 * (so the underlying URL never appears in DOM/history) and rendered with
 * react-pdf. All native browser download / print / context-menu affordances
 * are disabled. Server-side, the PDF is already watermarked per user via
 * ``apply_user_watermark`` — see ``backend/pdf/watermark.py``.
 */
export default function ReaderPage() {
  const { courseId, examId, fileId } = useParams();
  const navigate = useNavigate();
  const [pdfData, setPdfData] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [error, setError] = useState(null);

  // Fetch the PDF as a Blob so the "src" is a blob URL — no download URL
  // visible in HTML/inspector history, and no re-fetch on re-render.
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    const fetchPdf = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = examId
          ? `${API}/me/courses/${courseId}/exams/${examId}/theory/${fileId}`
          : `${API}/me/courses/${courseId}/general-theory/${fileId}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const detail = (await res.json().catch(() => ({})))?.detail || "No se pudo abrir el material";
          throw new Error(detail);
        }
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        // Feed react-pdf with a fresh ArrayBuffer so it doesn't hold a
        // reference to a blob URL (which the user could inspect and reuse).
        setPdfData({ data: buf });
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          toast.error(e.message);
        }
      }
    };
    fetchPdf();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [courseId, examId, fileId]);

  // Disable Ctrl+S / Ctrl+P / F12-based save shortcuts inside the reader page.
  useEffect(() => {
    const block = (e) => {
      const key = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (key === "s" || key === "p")) {
        e.preventDefault();
        toast.info("La descarga y la impresión están deshabilitadas.");
      }
    };
    const blockContext = (e) => {
      e.preventDefault();
    };
    window.addEventListener("keydown", block);
    window.addEventListener("contextmenu", blockContext);
    return () => {
      window.removeEventListener("keydown", block);
      window.removeEventListener("contextmenu", blockContext);
    };
  }, []);

  const options = useMemo(
    () => ({
      // Both fields are safe defaults; react-pdf memoizes on identity.
      disableStream: false,
      disableAutoFetch: false,
    }),
    [],
  );

  return (
    <div
      className="min-h-screen bg-neutral-900 text-white flex flex-col select-none"
      style={{ WebkitUserSelect: "none", userSelect: "none" }}
      data-testid="reader-page"
    >
      <header className="sticky top-0 z-10 bg-neutral-950 border-b border-neutral-800 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (window.opener ? window.close() : navigate("/dashboard"))}
            className="text-neutral-200 hover:text-white hover:bg-neutral-800"
            data-testid="reader-close"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex items-center gap-2 text-neutral-400 text-xs">
            <Lock className="w-3.5 h-3.5" />
            <span>Modo lectura — Descarga e impresión deshabilitadas</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)))}
            className="text-neutral-200 hover:bg-neutral-800"
            data-testid="reader-zoom-out"
            aria-label="Reducir"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs font-mono w-14 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setScale((s) => Math.min(2.5, +(s + 0.15).toFixed(2)))}
            className="text-neutral-200 hover:bg-neutral-800"
            data-testid="reader-zoom-in"
            aria-label="Ampliar"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((n) => Math.max(1, n - 1))}
            className="text-neutral-200 hover:bg-neutral-800"
            data-testid="reader-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs font-mono w-24 text-center">
            {pageNumber} / {numPages || "…"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={numPages != null && pageNumber >= numPages}
            onClick={() => setPageNumber((n) => (numPages ? Math.min(numPages, n + 1) : n + 1))}
            className="text-neutral-200 hover:bg-neutral-800"
            data-testid="reader-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6 flex justify-center">
        {error ? (
          <div className="mt-24 text-center max-w-md">
            <p className="text-red-400 mb-3">{error}</p>
            <Button onClick={() => navigate("/dashboard")} variant="outline">
              Volver al panel
            </Button>
          </div>
        ) : !pdfData ? (
          <div className="mt-24 flex flex-col items-center gap-3 text-neutral-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Cargando material…</span>
          </div>
        ) : (
          <div
            className="bg-white shadow-2xl"
            // Extra layer of "please don't screenshot" hint: forbid dragging
            // the rendered page image out of the viewport.
            onDragStart={(e) => e.preventDefault()}
            data-testid="reader-canvas"
          >
            <Document
              file={pdfData}
              options={options}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => setError(e?.message || "No se pudo renderizar el PDF")}
              loading={
                <div className="p-10 text-neutral-700">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                  Cargando…
                </div>
              }
              error={<div className="p-10 text-red-600">Error al renderizar el PDF</div>}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
        )}
      </main>

      <footer className="bg-neutral-950 border-t border-neutral-800 px-4 py-3 text-center text-[11px] text-neutral-500 leading-relaxed">
        © SIMA Asistencial SL · Material de estudio personalizado. <br className="sm:hidden" />
        Uso estrictamente personal — prohibida su reproducción, distribución o transmisión total o
        parcial por cualquier medio. Todas las páginas incluyen marca de agua trazable.
      </footer>
    </div>
  );
}
